import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text } from '../../../../components';
import { LumioGlyph } from '../../../creatives/components/LumioMark';
import { RunStatusLine } from '../../../creatives/components/StoryboardComposer/RunStatusLine';
import type { ArtStyle } from '../../art-style-api';
import type { ArtStyleBusy } from '../../useArtStyle';
import { Bar, ChangedHint, ExtractButton, CostTag, EndSentinel, FootHint } from './ExtractBar.styles';

export type ExtractBarProps = {
  style: ArtStyle;
  busy: ArtStyleBusy;
  /** Lumio is extracting this style (its request, then its job: it holds across a reload or a return visit). */
  extracting: boolean;
  /** The style is written but has nothing drawn since: the footer stays (Extract draws the samples) even with the
   *  ingredients unchanged. */
  missingSamples?: boolean;
  /** The newest extraction failed: the footer says so (in place of "Ingredients changed") and stays in view. */
  lastFailed?: boolean;
  /** Credits an extraction costs: it draws the four samples (undefined until the price table loads). */
  cost: number | undefined;
  /** Where it sits: the rail's sticky footer, or the ingredients sheet's pinned footer (the sheet scrolls its own body,
   *  so there it neither sticks nor veils). */
  placement?: 'rail' | 'sheet';
  onExtract: () => void;
};

/**
 * The ingredients' footer (the rail's, sticky, or the sheet's): "Extract style" with its cost, since an extraction also draws the four
 * samples; a typed status line while Lumio extracts, and what is missing before it can run. Once a style exists it
 * shows only when the ingredients changed since the last extraction ("Extract again", with a hint saying so). It
 * stays in view while the owner scrolls through the ingredients it reads.
 */
export const ExtractBar = ({
  style,
  busy,
  extracting,
  missingSamples = false,
  lastFailed = false,
  cost,
  placement = 'rail',
  onExtract,
}: ExtractBarProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const hasMood = style.mood_images.length > 0;
  const hasDefinition = style.definition.trim() !== '';
  const inputsChanged = hasDefinition && style.extraction !== null && style.inputs_sig !== style.extraction.inputs_sig;
  const canExtract = hasMood && busy === null && !extracting;
  // A failed extraction is the reason the ingredients read as changed (the record kept the earlier style), so the
  // footer names the failure instead.
  const failed = lastFailed && !extracting;

  // A style written from exactly these ingredients has nothing new to read: no "Extract again" until they change.
  const hidden = hasDefinition && !inputsChanged && !extracting && !missingSamples && !failed;

  // Whether the bar is pinned over ingredients (the end of the column is below the fold), which veils their edge.
  const inSheet = placement === 'sheet';
  const endRef = React.useRef<HTMLSpanElement>(null);
  const [veiled, setVeiled] = React.useState(false);
  React.useEffect(() => {
    const end = endRef.current;
    if (hidden || inSheet || !end || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setVeiled(!entry.isIntersecting));
    observer.observe(end);
    return () => observer.disconnect();
  }, [hidden, inSheet]);

  if (hidden) return null;

  return (
    <>
      <Bar $sheet={inSheet} $veiled={veiled}>
        {/* While Lumio reads the ingredients, what it is doing, typed in and cycled. */}
        {extracting ? (
          <RunStatusLine
            phrases={[
              t('Reading your mood board'),
              t('Studying the light, materials and colour'),
              t('Writing your style'),
            ]}
          />
        ) : null}
        {failed ? (
          <ChangedHint>
            <Icon name="info" size={14} />
            <Text text="The last extraction didn't finish. No credits were used." tag="span" />
          </ChangedHint>
        ) : inputsChanged && !extracting ? (
          <ChangedHint>
            <Icon name="info" size={14} />
            <Text text="Ingredients changed" tag="span" />
          </ChangedHint>
        ) : null}
        <ExtractButton
          type="button"
          disabled={!canExtract}
          title={!hasMood ? t('Add at least one mood board image first.') : undefined}
          onClick={() => {
            if (canExtract) onExtract();
          }}
        >
          <LumioGlyph size={16} />
          {hasDefinition ? <Text text="Extract again" tag="span" /> : <Text text="Extract style" tag="span" />}
          {cost != null ? (
            <CostTag text={cost === 1 ? '{{n}} credit' : '{{n}} credits'} placeholders={{ n: cost }} tag="span" />
          ) : null}
        </ExtractButton>
        {!hasMood ? (
          <FootHint text="Add at least one mood board image to extract a style." tag="span" />
        ) : extracting ? null : (
          <FootHint text="Lumio writes your style, then draws four samples in it." tag="span" />
        )}
      </Bar>
      {inSheet ? null : <EndSentinel ref={endRef} aria-hidden="true" />}
    </>
  );
};

export default ExtractBar;
